import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Carrot, Save, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import AdminNav from '../../components/admin/AdminNav';

const GROCERY_CATEGORIES = ['Produce', 'Proteins', 'Dairy', 'Pantry', 'Frozen', 'Other'];

// Create / edit a Foods Core record, including the two authoritative curated
// fields (grocery_category + allow_meal_scaling). For an intentionally created
// Core Food both are REQUIRED before saving. This is the ONLY place these are
// set — automatic USDA/FatSecret caching leaves them null until curated here.
export default function CoreFoodEditor() {
  const navigate = useNavigate();
  const { foodId } = useParams();
  const isNew = !foodId;

  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [foodName, setFoodName] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('');
  // '' = unclassified (backend null). Kept explicit so we can require it on create.
  const [groceryCategory, setGroceryCategory] = useState('');
  // '' = unset, 'yes' | 'no' -> boolean
  const [allowScaling, setAllowScaling] = useState('');
  // Neutral preference-group review
  const [taxonomy, setTaxonomy] = useState([]);
  const [prefGroups, setPrefGroups] = useState([]);
  const [reviewed, setReviewed] = useState(false);
  // Full-precision macro snapshot as first loaded. Used to send ONLY the macro
  // fields the admin actually changed, so a metadata-only save never rewrites
  // (and rounds) untouched nutrition values.
  const [initialMacros, setInitialMacros] = useState({ calories: '', protein: '', carbs: '', fat: '' });

  useEffect(() => {
    adminAPI.checkAdmin()
      .then(res => {
        if (res.data.is_admin) setIsAdmin(true);
        else { toast.error('Not authorized'); navigate('/home'); }
      })
      .catch(() => { toast.error('Not authorized'); navigate('/home'); })
      .finally(() => setCheckingAdmin(false));
  }, [navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    adminAPI.getPreferenceTaxonomy()
      .then(res => setTaxonomy(res.data.groups || []))
      .catch(() => setTaxonomy([]));
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || isNew) return;
    adminAPI.getCoreFood(foodId)
      .then(res => {
        const f = res.data;
        // Full-precision per-100g values (backend returns unrounded).
        const cal = f.macros_per_100g?.calories ?? '';
        const p = f.macros_per_100g?.protein ?? '';
        const c = f.macros_per_100g?.carbs ?? '';
        const ft = f.macros_per_100g?.fat ?? '';
        setFoodName(f.food_name || '');
        setCalories(String(cal));
        setProtein(String(p));
        setCarbs(String(c));
        setFat(String(ft));
        setInitialMacros({ calories: String(cal), protein: String(p), carbs: String(c), fat: String(ft) });
        setCategory(f.category || '');
        setSource(f.source || '');
        setGroceryCategory(f.grocery_category || '');
        setAllowScaling(f.allow_meal_scaling === true ? 'yes' : f.allow_meal_scaling === false ? 'no' : '');
        setPrefGroups(Array.isArray(f.preference_groups) ? f.preference_groups : []);
        setReviewed(f.preference_groups_reviewed === true);
      })
      .catch(() => { toast.error('Failed to load core food'); navigate('/admin/core-foods'); })
      .finally(() => setLoading(false));
  }, [isAdmin, isNew, foodId, navigate]);

  const toggleGroup = (g) => {
    setPrefGroups(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  const handleSave = async () => {
    if (!foodName.trim()) { toast.error('Food name is required'); return; }
    // Intentionally created Core Foods MUST be classified before saving.
    if (isNew && !groceryCategory) { toast.error('Grocery Category is required'); return; }
    if (isNew && allowScaling === '') { toast.error('Allow Meal Scaling is required'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          food_name: foodName.trim(),
          calories_per_100g: parseFloat(calories) || 0,
          protein_per_100g: parseFloat(protein) || 0,
          carbs_per_100g: parseFloat(carbs) || 0,
          fat_per_100g: parseFloat(fat) || 0,
          category: category || null,
          grocery_category: groceryCategory,
          allow_meal_scaling: allowScaling === 'yes',
          preference_groups: prefGroups,
          preference_groups_reviewed: reviewed,
        };
        const r = await adminAPI.createCoreFood(payload);
        toast.success('Core food created');
        navigate(`/admin/core-foods/${r.data.food_id}/edit`);
      } else {
        const payload = {
          food_name: foodName.trim(),
          category: category || null,
          grocery_category: groceryCategory || null,  // explicit null clears it
          allow_meal_scaling: allowScaling === 'yes' ? true : allowScaling === 'no' ? false : null,
          preference_groups: prefGroups,
          preference_groups_reviewed: reviewed,
        };
        // Only send macro fields the admin actually changed — an untouched
        // field is omitted entirely so its exact stored value is preserved.
        if (String(calories) !== initialMacros.calories) payload.calories_per_100g = parseFloat(calories) || 0;
        if (String(protein) !== initialMacros.protein) payload.protein_per_100g = parseFloat(protein) || 0;
        if (String(carbs) !== initialMacros.carbs) payload.carbs_per_100g = parseFloat(carbs) || 0;
        if (String(fat) !== initialMacros.fat) payload.fat_per_100g = parseFloat(fat) || 0;
        await adminAPI.updateCoreFood(foodId, payload);
        toast.success('Core food saved');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (checkingAdmin || loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="core-food-editor">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <Button onClick={() => navigate('/admin/core-foods')} variant="ghost" size="sm" className="rounded-full" data-testid="core-food-editor-back">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-2">
              <Carrot size={24} className="text-accent" />
              <h1 className="font-heading font-bold text-xl">{isNew ? 'New Core Food' : 'Edit Core Food'}</h1>
            </div>
          </div>
          <AdminNav />
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-6 max-w-2xl">
        <section className="space-y-3">
          <div>
            <Label>Food name</Label>
            <Input
              value={foodName}
              onChange={e => setFoodName(e.target.value)}
              placeholder="e.g. Bread (whole wheat)"
              className="mt-1"
              data-testid="core-food-name"
            />
          </div>
          {!isNew && source && (
            <p className="text-xs text-muted-foreground">Source: {source}</p>
          )}

          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Calories/100g</Label>
              <Input type="number" min="0" step="0.1" value={calories} onChange={e => setCalories(e.target.value)} className="h-9 mt-1" data-testid="core-food-calories" />
            </div>
            <div>
              <Label className="text-xs">Protein/100g</Label>
              <Input type="number" min="0" step="0.1" value={protein} onChange={e => setProtein(e.target.value)} className="h-9 mt-1" data-testid="core-food-protein" />
            </div>
            <div>
              <Label className="text-xs">Carbs/100g</Label>
              <Input type="number" min="0" step="0.1" value={carbs} onChange={e => setCarbs(e.target.value)} className="h-9 mt-1" data-testid="core-food-carbs" />
            </div>
            <div>
              <Label className="text-xs">Fat/100g</Label>
              <Input type="number" min="0" step="0.1" value={fat} onChange={e => setFat(e.target.value)} className="h-9 mt-1" data-testid="core-food-fat" />
            </div>
          </div>
        </section>

        {/* Authoritative curated classification */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase">Classification</h2>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Correcting a food here fixes it consistently for every recipe that uses it.
          </p>

          <div>
            <Label>Grocery Category{isNew && <span className="text-red-500"> *</span>}</Label>
            <select
              value={groceryCategory}
              onChange={e => setGroceryCategory(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1"
              data-testid="core-food-grocery-category"
            >
              <option value="">{isNew ? 'Select a category…' : 'Unclassified'}</option>
              {GROCERY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <Label>Allow Meal Scaling{isNew && <span className="text-red-500"> *</span>}</Label>
            <select
              value={allowScaling}
              onChange={e => setAllowScaling(e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-secondary/30 mt-1"
              data-testid="core-food-allow-scaling"
            >
              <option value="">{isNew ? 'Select…' : 'Unset'}</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              <AlertTriangle size={12} className="inline mr-1" />
              "No" blocks scaling for any meal where this is a required ingredient. It never forces
              a meal to scale — the scaling engine still decides which portions are valid.
            </p>
          </div>
        </section>

        {/* Neutral preference-group review (allergen/dislike taxonomy) */}
        <section className="space-y-3" data-testid="core-food-preference-review">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase">Preference Review</h2>
            {!reviewed && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600" data-testid="core-food-unreviewed-badge">
                Not reviewed
              </span>
            )}
            {reviewed && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">
                Reviewed
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Select every group that applies. An empty list is fine — but a meal cannot be published
            until each linked Core Food is explicitly marked reviewed.
          </p>

          <div className="flex flex-wrap gap-2">
            {taxonomy.map(g => {
              const on = prefGroups.includes(g);
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => toggleGroup(g)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    on ? 'bg-accent/15 text-accent border-accent/40'
                       : 'bg-secondary/30 text-muted-foreground border-transparent hover:bg-secondary/50'
                  }`}
                  data-testid={`pref-group-${g}`}
                >
                  {g.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>

          <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={reviewed}
              onChange={e => setReviewed(e.target.checked)}
              className="h-4 w-4"
              data-testid="core-food-reviewed-checkbox"
            />
            <span className="text-sm">Mark preference groups as reviewed</span>
          </label>
        </section>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 rounded-xl bg-accent text-white"
          data-testid="core-food-save-btn"
        >
          <Save size={18} className="mr-2" />
          {saving ? 'Saving…' : (isNew ? 'Create Core Food' : 'Save Changes')}
        </Button>
      </div>
    </div>
  );
}
